import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Shield, Heart, Clock, Users, Star, ChevronDown } from 'lucide-react';

const heroImage = "https://images.unsplash.com/photo-1704475686860-ab71b444df6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxjb21wYXNzaW9uYXRlJTIwY2FyZWdpdmVyJTIwc2VuaW9yJTIwZWxkZXJseSUyMHdhcm0lMjBzbWlsZXxlbnwwfHx8fDE3NzU0NTUzMTl8MA&ixlib=rb-4.1.0&q=85";

const stats = [
  { value: '15+', label: 'Years of Service', icon: Clock },
  { value: '5,000+', label: 'Families Served', icon: Users },
  { value: '98%', label: 'Satisfaction Rate', icon: Star },
];

export const HeroSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-warm-beige"
      data-testid="hero-section"
    >
      {/* Background Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-soft-gold/20 to-transparent rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, 40, 0],
            x: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute -bottom-60 -left-40 w-[800px] h-[800px] bg-gradient-to-tr from-deep-blue/10 to-transparent rounded-full blur-3xl"
        />
      </div>

      <motion.div style={{ y, opacity }} className="relative w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-28 sm:pt-32 pb-16 sm:pb-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center lg:text-left"
            >
              {/* Overline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center space-x-2 bg-soft-gold/10 border border-soft-gold/20 rounded-full px-3 sm:px-4 py-2 mb-6 sm:mb-8"
              >
                <Heart className="w-4 h-4 text-soft-gold" />
                <span className="text-xs sm:text-sm font-medium text-soft-gold tracking-wide">
                  Compassionate In-Home Care
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="font-serif text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium text-navy leading-[1.1] mb-5 sm:mb-6"
              >
                Care That Feels{' '}
                <span className="text-gradient-gold">Like Family</span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0"
              >
                Heritage Home Health LLC provides personalized, compassionate in-home care 
                that honors your loved one's dignity, independence, and well-being.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link
                  to="/contact"
                  data-testid="hero-contact-button"
                  className="group inline-flex items-center justify-center space-x-2 bg-navy text-white px-6 sm:px-8 py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-deep-blue transition-all duration-300 shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1 w-full sm:w-auto"
                >
                  <span className="sm:hidden">Contact Us</span>
                  <span className="hidden sm:inline">Contact Heritage Home Health LLC</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/what-we-do"
                  data-testid="hero-learn-more-button"
                  className="inline-flex items-center justify-center space-x-2 bg-white text-navy px-6 sm:px-8 py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-soft-gray transition-all duration-300 border border-soft-gray shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1 w-full sm:w-auto"
                >
                  <span>Explore Our Services</span>
                </Link>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="mt-10 sm:mt-12 flex flex-wrap items-center gap-4 sm:gap-6 justify-center lg:justify-start"
              >
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-soft-gold to-soft-gold/60 border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div className="text-left">
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-soft-gold text-soft-gold" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Trusted by thousands of families
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image with Floating Cards */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 1 }}
              className="relative"
            >
              {/* Main Image */}
              <div className="relative z-10">
                <div className="relative rounded-3xl overflow-hidden shadow-soft-3d-lg">
                  <img
                    src={heroImage}
                    alt="Compassionate caregiver with elderly person"
                    className="w-full h-auto object-cover aspect-[4/5] md:aspect-[3/4]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/30 via-transparent to-transparent" />
                </div>

                {/* Floating Stats Card */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="hidden md:block absolute -left-8 md:-left-16 bottom-20 glass shadow-soft-3d-lg rounded-2xl p-5 animate-float"
                  style={{ animationDelay: '0.5s' }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-soft-gold/20 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-soft-gold" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy">15+</p>
                      <p className="text-sm text-gray-500">Years Trusted</p>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Badge */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1, duration: 0.6 }}
                  className="hidden md:block absolute -right-4 md:-right-12 top-20 glass shadow-soft-3d-lg rounded-2xl p-4 animate-float"
                  style={{ animationDelay: '1s' }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-muted-green/20 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-muted-green" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-navy">98%</p>
                      <p className="text-xs text-gray-500">Happy Families</p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Background Decorative Element */}
              <div className="absolute -z-10 top-6 sm:top-10 right-4 sm:right-10 w-full h-full rounded-3xl bg-gradient-to-br from-soft-gold/30 to-muted-green/20" />
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <button
          onClick={() => {
            const servicesSection = document.getElementById('services-section');
            if (servicesSection) {
              const headerOffset = 100;
              const elementPosition = servicesSection.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          }}
          className="flex flex-col items-center text-gray-400 hover:text-soft-gold transition-colors cursor-pointer"
          data-testid="scroll-indicator"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center"
          >
            <span className="text-xs tracking-widest uppercase mb-2">Scroll</span>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </button>
      </motion.div>
    </section>
  );
};

export default HeroSection;
